import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import API from "../../../../../global/api";
import { changeDateFormate, PDFHeader } from "../../../../../global/globalFunctions";


export const ACTION_TYPES = {
    REQUEST: "REQUEST_PROFIT_AND_LOSS_PDF",
    SUCCESS: "SUCCESS_PROFIT_AND_LOSS_PDF",
    ERROR: "ERROR_PROFIT_AND_LOSS_PDF",
};

export const requestProfitAndLossPDF = () => ({ type: ACTION_TYPES.REQUEST });
export const successProfitAndLossPDF = (data) => ({
    type: ACTION_TYPES.SUCCESS,
    payload: data,
});
export const errorProfitAndLossPDF = (error) => ({
    type: ACTION_TYPES.ERROR,
    payload: error,
});

export const ProfitAndLossPDFAPi = (filters) => async (dispatch) => {
    dispatch(requestProfitAndLossPDF());
    try {
        const { data: getData } = await API.post("/reports/profit_loss", filters);
        dispatch(successProfitAndLossPDF(getData));
        generateProfitAndLossPDF(getData.data, filters);
        return getData;
    } catch (error) {
        dispatch(errorProfitAndLossPDF(error?.message));
        throw error;
    }
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtNum = (n) => {
    const val = Number(n) || 0;
    const abs = Math.abs(val).toLocaleString("en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return val < 0 ? `(${abs})` : abs;
};

// Returns a signed balance.
// Income section:  Credit balance → positive, Debit → negative
// Cost/Expense:    Debit balance  → positive, Credit → negative
const signedBalance = (balance, sign, isIncomeSection) => {
    const isCredit = sign === "Credit" || Number(sign) === 1;
    if (isIncomeSection) return isCredit ? balance : -balance;
    return isCredit ? -balance : balance;
};

const cell = (text, styles = {}) => ({ content: text, styles });
const amountCell = (n, extra = {}) => cell(fmtNum(n), { halign: "right", ...extra });
const boldCell = (text, extra = {}) => cell(text, { fontStyle: "bold", ...extra });
const boldAmountCell = (n, extra = {}) => amountCell(n, { fontStyle: "bold", ...extra });

const INDENT = "    ";  // 4 spaces per level

// Appends rows for one L2 and all its children
const addL2Rows = (l2, rows, isIncomeSection, depth = 1) => {
    const l3List = l2.levelThreeArray || [];
    const l2Signed = signedBalance(l2.levelBalance, l2.levelBalanceSign, isIncomeSection);
    const pad = INDENT.repeat(depth);

    if (l3List.length === 0) {
        rows.push([cell(`${pad}${l2.levelTwoName}`), amountCell(l2Signed)]);
        return;
    }

    rows.push([cell(`${pad}${l2.levelTwoName}`), cell("")]);

    for (const l3 of l3List) {
        const l4List = l3.levelFourArray || [];
        const l3Signed = signedBalance(l3.levelBalance, l3.levelBalanceSign, isIncomeSection);
        const pad2 = INDENT.repeat(depth + 1);

        if (l4List.length === 0) {
            rows.push([cell(`${pad2}${l3.levelThreeName}`), amountCell(l3Signed)]);
        } else {
            rows.push([cell(`${pad2}${l3.levelThreeName}`), cell("")]);

            for (const l4 of l4List) {
                const l4Signed = signedBalance(
                    l4.levelBalance,
                    l4.transactions?.[0]?.closing_balance_sign,
                    isIncomeSection
                );
                rows.push([
                    cell(`${INDENT.repeat(depth + 2)}${l4.levelFourName}`),
                    amountCell(l4Signed),
                ]);
            }

            rows.push([
                boldCell(`${pad}Total ${l3.levelThreeName}`),
                boldAmountCell(l3Signed),
            ]);
        }
    }

    rows.push([
        boldCell(`${pad}Total ${l2.levelTwoName}`),
        boldAmountCell(l2Signed),
    ]);
};

// ─── PDF Generation ─────────────────────────────────────────────────────────

const generateProfitAndLossPDF__Gernal = (getData, filters) => {
    const doc = new jsPDF("p", "pt", "A4");
    PDFHeader(doc, "Profit and Loss");

    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
        `Date From: ${changeDateFormate(filters.startDate)}   To: ${changeDateFormate(filters.endDate)}`,
        pageWidth / 2,
        120,
        { align: "center" }
    );

    const UP = (s) => (s || "").toUpperCase();

    // Classify L1 accounts by name keywords
    const incomeL1 = getData.filter((l1) => UP(l1.name).includes("INCOME"));

    const cogsL1 = getData.filter(
        (l1) =>
            (UP(l1.name).includes("COST") ||
                UP(l1.name).includes("COGS") ||
                UP(l1.name).includes("PURCHASE")) &&
            !UP(l1.name).includes("INCOME")
    );

    const expenseL1 = getData.filter(
        (l1) =>
            UP(l1.name).includes("EXPENSE") &&
            !UP(l1.name).includes("INCOME")
    );

    const rows = [];
    let totalIncome = 0;
    let totalCOGS = 0;
    let totalExpenses = 0;

    const sectionBg = [230, 230, 230];
    const subtotalBg = [245, 245, 245];
    const grossBg = [210, 240, 210];
    const netBg = [210, 225, 245];

    // ── Section header ─────────────────────────────────────────────────
    rows.push([
        boldCell("Ordinary Income/Expense", { fillColor: sectionBg }),
        cell("", { fillColor: sectionBg }),
    ]);

    // ── Income ─────────────────────────────────────────────────────────
    if (incomeL1.length > 0) {
        rows.push([boldCell("  Income"), cell("")]);

        for (const l1 of incomeL1) {
            for (const l2 of l1.levelTwoArray || []) {
                addL2Rows(l2, rows, true);
            }
            totalIncome += signedBalance(l1.levelBalance, l1.levelBalanceSign, true);
        }

        rows.push([
            boldCell("Total Income", { fillColor: subtotalBg }),
            boldAmountCell(totalIncome, { fillColor: subtotalBg }),
        ]);
    }

    // ── Cost of Goods Sold ─────────────────────────────────────────────
    if (cogsL1.length > 0) {
        rows.push([boldCell("  Cost of Goods Sold"), cell("")]);

        for (const l1 of cogsL1) {
            for (const l2 of l1.levelTwoArray || []) {
                addL2Rows(l2, rows, false);
            }
            totalCOGS += signedBalance(l1.levelBalance, l1.levelBalanceSign, false);
        }

        rows.push([
            boldCell("Total COGS", { fillColor: subtotalBg }),
            boldAmountCell(totalCOGS, { fillColor: subtotalBg }),
        ]);
    }

    // ── Gross Profit ───────────────────────────────────────────────────
    const grossProfit = totalIncome - totalCOGS;
    rows.push([
        boldCell("Gross Profit", { fillColor: grossBg }),
        boldAmountCell(grossProfit, { fillColor: grossBg }),
    ]);

    // ── Expenses ───────────────────────────────────────────────────────
    if (expenseL1.length > 0) {
        rows.push([boldCell("  Expenses"), cell("")]);

        for (const l1 of expenseL1) {
            for (const l2 of l1.levelTwoArray || []) {
                addL2Rows(l2, rows, false);
            }
            totalExpenses += signedBalance(l1.levelBalance, l1.levelBalanceSign, false);
        }

        rows.push([
            boldCell("Total Expenses", { fillColor: subtotalBg }),
            boldAmountCell(totalExpenses, { fillColor: subtotalBg }),
        ]);
    }

    // ── Net Income ─────────────────────────────────────────────────────
    const netIncome = grossProfit - totalExpenses;
    rows.push([
        boldCell("Net Ordinary Income", { fillColor: netBg }),
        boldAmountCell(netIncome, { fillColor: netBg }),
    ]);
    rows.push([
        boldCell("Net Income", { fillColor: netBg }),
        boldAmountCell(netIncome, { fillColor: netBg }),
    ]);

    // ── Render table ───────────────────────────────────────────────────
    doc.autoTable({
        startY: 135,
        head: [["Account", "Amount"]],
        body: rows,
        theme: "plain",
        margin: { left: 40, right: 40 },
        columnStyles: {
            0: { cellWidth: 375 },
            1: { cellWidth: 140, halign: "right" },
        },
        headStyles: {
            fillColor: [41, 128, 185],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
            halign: "center",
        },
        styles: {
            fontSize: 9,
            cellPadding: { top: 2, right: 6, bottom: 2, left: 4 },
            lineColor: [210, 210, 210],
            lineWidth: 0.2,
        },
    });

    doc.setProperties({
        title: `Profit and Loss - ${moment().format("YYYY-MM-DD")}.pdf`,
    });
    doc.output("dataurlnewwindow");
};

const generateProfitAndLossPDF = (getData, filters) => {
    const doc = new jsPDF("p", "pt", "A4");
    PDFHeader(doc, "Profit and Loss");
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
        `Date From: ${changeDateFormate(filters.startDate)}   To: ${changeDateFormate(filters.endDate)}`,
        pageWidth / 2, 120, { align: "center" }
    );


    const totalSales = getData.find((x) => x.levelFourName === "SALES").closingBalance || 0;
    const purchaseDiscount = getData.find((x) => x.levelFourName === "PURCHASE DISCOUNT").closingBalance || 0;
    const leakDiscount = getData.find((x) => x.levelFourName === "LEAK DISCOUNT").closingBalance || 0;
    const adjustmentExpense = getData.find((x) => x.levelFourName === "ADJUSTMENT EXPENSE").closingBalance || 0;
    const totalDiscountIncome = purchaseDiscount + leakDiscount + adjustmentExpense;
    const totalIncome = totalSales + totalDiscountIncome;
    const materialPurchases = getData.find((x) => x.levelFourName === "MATERIAL PURCHASES").closingBalance || 0;
    const purchaseTax = getData.find((x) => x.levelFourName === "PURCHASE TAX 0.1 %").closingBalance || 0;
    const freight = getData.find((x) => x.levelFourName === "FREIGHT").closingBalance || 0;
    const salesDiscount = getData.find((x) => x.levelFourName === "DISCOUNT ALLOWED (SALES DISCOUNT)").closingBalance || 0;
    const collectionDiscount = getData.find((x) => x.levelFourName === "COLLECTION DISCOUNT").closingBalance || 0;
    const totalOtherExpenses = purchaseTax + freight + salesDiscount + collectionDiscount;
    const netProfit = totalIncome - materialPurchases - totalOtherExpenses;


    // 3-column layout: [Label | Sub-amount (cost items) | Total (main figures)]
    const mk = (label, sub, total, styles = {}) => [
        { content: label, styles },
        { content: sub !== null ? fmtNum(sub) : "", styles: { halign: "right", ...styles } },
        { content: total !== null ? fmtNum(total) : "", styles: { halign: "right", ...styles } },
    ];
    const mkBold = (label, sub, total, bg) => {
        const s = { fontStyle: "bold", ...(bg ? { fillColor: bg } : {}) };
        return [
            { content: label, styles: s },
            { content: sub !== null ? fmtNum(sub) : "", styles: { halign: "right", ...s } },
            { content: total !== null ? fmtNum(total) : "", styles: { halign: "right", ...s } },
        ];
    };
    const mkEmpty = () => [{ content: "" }, { content: "" }, { content: "" }];
    const mkHeader = (label) => [
        { content: label, styles: { fontStyle: "bold" } },
        { content: "" }, { content: "" },
    ];

    const subtotalBg = [245, 245, 245];
    const grossBg = [210, 240, 210];
    const lossBg = [255, 220, 220];

    const rows = [];

    // ── Sales section ──────────────────────────────────────────────────────
    rows.push(mkBold("SALES", null, totalSales));
    rows.push(mkEmpty());

    rows.push(mkHeader("Add:-"));
    rows.push(mk("    PURCHASE DISCOUNT", purchaseDiscount, null));
    rows.push(mk("    LEAK DISCOUNT", leakDiscount, null));
    rows.push(mk("    ADJUSTMENT EXPENSE", adjustmentExpense, null));
    rows.push(mkBold("TOTAL DISCOUNT INCOME", totalDiscountIncome, totalDiscountIncome, subtotalBg));
    rows.push(mkBold("TOTAL INCOME", null, totalIncome, subtotalBg));
    rows.push(mkEmpty());

    rows.push(mkHeader("Less Expenses:-"));
    rows.push(mk("    MATERIAL PURCHASES", materialPurchases, null));
    rows.push(mkBold("TOTAL MATERIAL PURCHASES", materialPurchases, -materialPurchases, subtotalBg));
    rows.push(mkEmpty());

    rows.push(mkHeader("Less Other Expenses:-"));
    rows.push(mk("    PURCHASE TAX 0.1 %", purchaseTax, null));
    rows.push(mk("    FREIGHT", freight, null));
    rows.push(mk("    SALES DISCOUNT", salesDiscount, null));
    rows.push(mk("    COLLECTION DISCOUNT", collectionDiscount, null));
    rows.push(mkBold("TOTAL OTHER EXPENSES", totalOtherExpenses, -totalOtherExpenses, subtotalBg));
    rows.push(mkEmpty());

    rows.push(mkBold("NET PROFIT", null, netProfit, netProfit >= 0 ? grossBg : lossBg));

    doc.autoTable({
        startY: 135,
        head: [["Account", "Amount", "Total"]],
        body: rows,
        theme: "plain",
        margin: { left: 40, right: 40 },
        columnStyles: {
            0: { cellWidth: 295 },
            1: { cellWidth: 105, halign: "right" },
            2: { cellWidth: 115, halign: "right" },
        },
        headStyles: {
            fillColor: [41, 128, 185],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
            halign: "center",
        },
        styles: {
            fontSize: 9,
            cellPadding: { top: 3, right: 6, bottom: 3, left: 4 },
            lineColor: [210, 210, 210],
            lineWidth: 0.2,
        },
    });


    doc.setProperties({ title: `Profit and Loss - ${moment().format("YYYY-MM-DD")}.pdf` });
    var blob = doc.output("blob");
    window.open(URL.createObjectURL(blob));
};
